import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  SentIcon,
  Cancel01Icon,
  PencilEdit02Icon,
  RefreshIcon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { IconAI } from "shared/components/ai/IconAI";
import { formatPhone } from "shared/utils";
import { MessageCardLayout } from "./MessageCard";
import type { SuggestedReply } from "../types";

interface SuggestedReplyCardProps {
  suggestion: SuggestedReply;
  onSend: (text: string) => void;
  onDismiss: () => void;
  onRetry?: () => void;
  isRetrying?: boolean;
  isSending?: boolean;
  sendDisabled?: boolean;
  sendDisabledReason?: string;
  customerPhone?: string;
}

const SuggestedReplyHeader = ({
  customerPhone
}: {
  customerPhone?: string;
}) => (
  <div className="flex items-center gap-2 text-sm font-medium text-content-200">
    <span>Leasing agent</span>
    <HugeiconsIcon
      icon={InformationCircleIcon}
      size={16}
      className="text-content-200"
    />
    <span>
      Based on: prior thread
      {customerPhone ? ` with ${formatPhone(customerPhone)}` : ""}
    </span>
  </div>
);

export const SuggestedReplyCard = ({
  suggestion,
  onSend,
  onDismiss,
  onRetry,
  isRetrying,
  isSending,
  sendDisabled,
  sendDisabledReason,
  customerPhone
}: SuggestedReplyCardProps) => {
  const { status, text, failed_reason } = suggestion;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(text ?? "");

  // Keep the local draft in sync if the suggestion text changes (e.g. a
  // regeneration arrives via WebSocket) while the user hasn't started editing.
  useEffect(() => {
    if (!isEditing) setDraft(text ?? "");
  }, [text, isEditing]);

  return (
    <MessageCardLayout
      isFromContact={false}
      avatar={<IconAI bgSize={32} />}
      header={<SuggestedReplyHeader customerPhone={customerPhone} />}
    >
      <div className="ai-animated-border w-full max-w-[580px] rounded-2xl rounded-tr-none overflow-hidden">
        {/* Body */}
        <div className="px-5 py-4">
          {status === "generating" && (
            <div className="flex items-center gap-2 text-content-300">
              <span className="size-4 animate-spin rounded-full border-2 border-alpha-200 border-t-primary-500" />
              <span className="text-base">Generating response…</span>
            </div>
          )}

          {status === "failed" && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-danger-500">
                <HugeiconsIcon icon={AlertCircleIcon} size={20} />
                <span className="text-base font-medium">
                  {failed_reason || "Couldn't generate a suggestion"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {onRetry ? (
                  <Button
                    HugeIcon={RefreshIcon}
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    onClick={onRetry}
                    loading={isRetrying}
                  >
                    Retry
                  </Button>
                ) : null}
                <Button
                  HugeIcon={Cancel01Icon}
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  iconStrokeWidth={3}
                  onClick={onDismiss}
                  disabled={isRetrying}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          {status === "ready" &&
            (isEditing ? (
              <textarea
                aria-label="Edit suggested reply"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                rows={4}
                autoFocus
                disabled={isSending}
                className="w-full resize-y rounded-lg border border-alpha-200 bg-white p-3 text-base leading-relaxed text-content-1000 outline-none focus:border-primary-500"
              />
            ) : (
              <p className="whitespace-pre-wrap text-base leading-relaxed text-content-1000">
                {draft}
              </p>
            ))}
        </div>

        {/* Actions */}
        {status === "ready" && (
          <div className="flex items-center justify-between gap-3 px-5 pb-4">
            <div className="flex items-center gap-3">
              <Button
                HugeIcon={SentIcon}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                onClick={() => onSend(draft)}
                loading={isSending}
                disabled={
                  isSending || draft.trim().length === 0 || sendDisabled
                }
                tooltip={sendDisabled ? sendDisabledReason : undefined}
              >
                Send
              </Button>

              <Button
                HugeIcon={isEditing ? Cancel01Icon : PencilEdit02Icon}
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={() => {
                  if (isEditing) {
                    // Discard edits and restore the AI draft.
                    setDraft(text ?? "");
                    setIsEditing(false);
                  } else setIsEditing(true);
                }}
                disabled={isSending}
              >
                {isEditing ? "Cancel" : "Edit"}
              </Button>
            </div>

            <Button
              HugeIcon={Cancel01Icon}
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              iconStrokeWidth={3}
              onClick={onDismiss}
              disabled={isSending}
            >
              Dismiss
            </Button>
          </div>
        )}
      </div>
    </MessageCardLayout>
  );
};
